import { X } from 'lucide-react';
import { WrestlerCard } from './WrestlerCard';
import { SectionHeader } from '@/components/ui';

interface StatsSheetProps {
    isOpen: boolean;
    onClose: () => void;
    stats: any; // We'll refine this type
}

export function StatsSheet({ isOpen, onClose, stats }: StatsSheetProps) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-fade-in">
            <div className="relative w-full max-w-lg bg-bg/90 backdrop-blur-xl rounded-3xl border border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden max-h-[90vh] overflow-y-auto ring-1 ring-white/5">

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/5 transition-colors z-10"
                >
                    <X size={20} className="text-white" />
                </button>

                <div className="p-6 md:p-8 pb-12">
                    <h2 className="text-xl font-bold uppercase tracking-wide text-center mb-6">Wrestler DNA</h2>

                    {/* The Original Card */}
                    <div className="flex justify-center mb-8">
                        <WrestlerCard />
                    </div>

                    {/* Stats Breakdown */}
                    <div className="space-y-6">
                        <SectionHeader title="Attribute Analysis" subtitle="Based on your training data" />

                        <div className="space-y-4">
                            <AttributeRow
                                label="Offense"
                                value={stats.subRatings.offense}
                                desc="Takedown mastery & neutral attacks"
                            />
                            <AttributeRow
                                label="Defense"
                                value={stats.subRatings.defense}
                                desc="Sprawls, counters & mat awareness"
                            />
                            <AttributeRow
                                label="Top Game"
                                value={stats.subRatings.topGame}
                                desc="Control, rides & turns"
                            />
                            <AttributeRow
                                label="Bottom"
                                value={stats.subRatings.bottomGame}
                                desc="Escapes & reversals"
                            />
                            <AttributeRow
                                label="Mat IQ"
                                value={stats.subRatings.matIQ}
                                desc="Technique variety & situational knowledge"
                            />
                        </div>
                    </div>

                    {/* Archetype Explanation */}
                    <div className="mt-8">
                        <div className="p-6 rounded-[20px] bg-gradient-to-br from-bg-surface to-bg-elevated border border-gold-base/10 relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-4 opacity-10">
                                <TrophyIcon />
                            </div>

                            <h3 className="text-[14px] font-bold text-gold-base uppercase tracking-wider mb-2">
                                {stats.archetype}
                            </h3>
                            <p className="text-[13px] text-text-secondary leading-relaxed">
                                Your training patterns show a focus on this style.
                                {stats.archetype === 'Takedown Artist' && " You prioritize neutral attacks and aggressive shots."}
                                {stats.archetype === 'Iron Wall' && " Your defense is becoming impenetrable."}
                                {stats.archetype === 'Mat Rat' && " You excel at controlling the pace on the mat."}
                                {stats.archetype === 'Technician' && " You have a deep bag of tricks and high wrestling IQ."}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function AttributeRow({ label, value, desc }: { label: string, value: number, desc: string }) {
    return (
        <div className="flex items-center gap-4">
            <div className="w-12 text-right">
                <span className="text-[16px] font-bold text-white">{value}</span>
            </div>
            <div className="flex-1">
                <div className="flex justify-between items-baseline mb-1">
                    <span className="text-[13px] font-semibold text-text-primary">{label}</span>
                </div>
                {/* Progress Bar */}
                <div className="h-1.5 w-full bg-bg-surface2 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-gold-base rounded-full"
                        style={{ width: `${value}%` }}
                    />
                </div>
                <span className="text-[10px] text-text-muted mt-1 block">{desc}</span>
            </div>
        </div>
    );
}

const TrophyIcon = () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" className="text-white">
        <path d="M12 2C15.866 2 19 5.13401 19 9C19 12.866 15.866 16 12 16C8.13401 16 5 12.866 5 9C5 5.13401 8.13401 2 12 2ZM12 4C9.23858 4 7 6.23858 7 9C7 11.7614 9.23858 14 12 14C14.7614 14 17 11.7614 17 9C17 6.23858 14.7614 4 12 4ZM12 18C16.4183 18 20 21.5817 20 26H4C4 21.5817 7.58172 18 12 18Z" />
    </svg>
);
